import { Modal } from "antd";
import React, { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import { Alert, Button, Col, Row } from "reactstrap";
import { NormMaterialType } from "./NormCreation";
import { ReactTyped } from "react-typed";

// Wastage is entered as a PERCENT here (e.g. 2 => 2%). NormCreation converts
// it to a fraction (0.02) when building the API payload, and back to a
// percent when loading a norm for edit.
//
// NOTE: this modal only ever adds a new line or edits a line that has not
// been saved yet (no normMaterialId). Saved lines are immutable — they can
// only be deactivated/activated from the materials table in NormCreation.
const NormMaterialModal: FC<{
  open: boolean;
  title: "Add New Material" | "Edit Material";
  mode: "create" | "edit" | "view";
  onCancel: () => void;
  append: (data: any) => void;
  update: (index: any, data: any) => void;
  extra?: {
    index?: number;
    defaultData?: any;
    sourceData?: any;
    materialOptions?: SelectOption[];
    uomOptions?: SelectOption[];
  };
}> = ({ open, title, mode, onCancel, append, update, extra }) => {
  const [warningMaterial, setWarningMaterial] = useState<{
    index: number | undefined;
    message: string | undefined;
  }>({
    index: undefined,
    message: undefined,
  });
  const methods = useForm<NormMaterialType>({
    mode: "onChange",
    defaultValues: {
      ...extra?.defaultData,
    },
  });

  const { handleSubmit, setValue } = methods;

  const resetWarningMaterial = () => {
    setWarningMaterial({
      index: undefined,
      message: undefined,
    });
  };

  const validateMaterials = (
    source: NormMaterialType[],
    target: NormMaterialType
  ) => {
    try {
      const { materialId } = target;
      for (let i = 0; i < source?.length; i++) {
        const material = source[i];
        // A deactivated saved line doesn't block re-adding the material —
        // "deactivate + add a new line" is the correction workflow.
        if (material.status === "Inactive") continue;
        if (material.materialId === materialId) {
          setWarningMaterial({
            index: source.length,
            message: "This material is already added to the norm.",
          });
          return false;
        }
      }
      return true;
    } catch (error) {
      console.log("error", error);
      return false;
    }
  };

  return (
    <Modal
      maskClosable={false}
      title={title}
      open={open}
      footer={null}
      width={"90%"}
      onCancel={() => onCancel()}
    >
      <FormProvider {...methods}>
        <form>
          <div className={` mb-3 p-2 `}>
            <div className="w-100">
              <Row className="w-100 mt-1">
                <Col lg="6">
                  <FormSelect
                    name={`materialId`}
                    label="Material"
                    options={extra?.materialOptions || []}
                    required={true}
                    onChange={(_, option) => {
                      setValue("materialName", option?.label);
                      setValue("materialCode", option?.extra?.materialCode);
                      // Auto-fill UOM & unit price from the material master
                      // when the data supports it (still editable below).
                      if (option?.extra?.uomId) {
                        setValue("uomId", option?.extra?.uomId, {
                          shouldDirty: true,
                        });
                        setValue("uomName", option?.extra?.uomName);
                      }
                      if (
                        option?.extra?.unitPrice !== undefined &&
                        option?.extra?.unitPrice !== null
                      ) {
                        setValue("unitPrice", option?.extra?.unitPrice, {
                          shouldDirty: true,
                        });
                      }
                      resetWarningMaterial();
                    }}
                  />
                </Col>
                <Col lg="3">
                  <FormSelect
                    name={`uomId`}
                    label="UOM"
                    options={extra?.uomOptions || []}
                    onChange={(_, option) => {
                      setValue("uomName", option?.label);
                    }}
                  />
                </Col>
                <Col lg="3">
                  <FormInput
                    name={`consumptionNorm`}
                    label="Consumption Norm (Per Unit)"
                    type="number"
                    pattern={/^\d+(\.\d{1,8})?$/}
                    required={true}
                    customValidation={{
                      validate: (value: any) =>
                        (Number(value) > 0 &&
                          /^\d+(\.\d{1,8})?$/.test(String(value))) ||
                        "Consumption norm must be greater than 0 (max 8 decimals)",
                    }}
                  />
                </Col>
              </Row>
              <Row className="w-100 mt-2">
                <Col lg="3">
                  <FormInput
                    name={`wastage`}
                    label="Wastage (%)"
                    type="number"
                    pattern={/^\d+(\.\d+)?$/}
                    min={0}
                    placeholder="e.g. 2 for 2%"
                  />
                </Col>
                <Col lg="3">
                  <FormInput
                    name={`unitPrice`}
                    label="Unit Price (₹)"
                    type="number"
                    pattern={/^\d+(\.\d{1,4})?$/}
                    min={0}
                    customValidation={{
                      validate: (value: any) =>
                        value === undefined ||
                        value === null ||
                        value === "" ||
                        /^\d+(\.\d{1,4})?$/.test(String(value)) ||
                        "Unit price allows max 4 decimals",
                    }}
                  />
                </Col>
                <Col lg="3">
                  <FormInput
                    name={`pucFactor`}
                    label="PUC Factor"
                    type="number"
                    pattern={/^\d+(\.\d+)?$/}
                    min={0}
                    placeholder="Purchase unit conversion, default 1"
                  />
                </Col>
              </Row>
            </div>
            <Alert
              isOpen={warningMaterial?.index !== undefined}
              color="danger"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <ReactTyped
                strings={[warningMaterial?.message || ""]}
                typeSpeed={15}
                showCursor={false}
              />
            </Alert>
          </div>
          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                {title === "Edit Material" ? (
                  <li>
                    <Button
                      className="btn btn-primary btn-sm"
                      type="button"
                      color="primary"
                      onClick={handleSubmit((data) => {
                        const fields = extra?.sourceData;
                        const isValid =
                          fields?.length > 0
                            ? validateMaterials(
                                fields?.filter(
                                  (_: any, idx: any) => idx != extra?.index
                                ),
                                data
                              )
                            : true;
                        if (!isValid) return;
                        // Unsaved line — replace the whole row locally; it
                        // goes to newMaterials wholesale on save.
                        update(extra?.index, data);
                        onCancel();
                      })}
                    >
                      Update Material
                    </Button>
                  </li>
                ) : (
                  <li>
                    <Button
                      className="btn btn-primary btn-sm"
                      type="button"
                      color="primary"
                      onClick={handleSubmit((data) => {
                        const fields = extra?.sourceData;
                        const isValid =
                          fields?.length > 0
                            ? validateMaterials(fields, data)
                            : true;
                        if (!isValid) return;
                        append(data);
                        onCancel();
                      })}
                    >
                      Add Material
                    </Button>
                  </li>
                )}
                <li>
                  <a
                    href="#cancel"
                    onClick={(ev) => {
                      ev.preventDefault();
                      onCancel();
                    }}
                    className="link link-light"
                  >
                    Cancel
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default NormMaterialModal;
